import React, { FC, useContext, useEffect, useRef } from 'react'

import { useFormContext } from 'react-hook-form'

import { MAX_MONTH_NUMBER, MIN_MONTH_NUMBER } from 'Const'
import { useCustomer } from 'Hooks/checkout'
import { useLocationIsUS } from 'Hooks/location'
import useEvent from 'Hooks/useEvent'
import useInputMask from 'Hooks/useInputMask'

import ErrorMessage from 'Components/ErrorMessage'
import SpinnerWheel from 'Components/SpinnerWheel'
import { Context, CVVInput, NumberInput } from 'Components/Spreedly'

import FormErrors from '../../../FormErrors'
import { usePayment } from '../../Provider'

import styles from './index.module.less'

const TWO_FIRST_YEAR_DIGITS = new Date().getUTCFullYear() % 100

const SpreedlyPaymentFields: FC = () => {
  const { paymentStatus, gateway } = usePayment()
  const { transferFocus } = useContext(Context)
  const isUSLocation = useLocationIsUS()
  const customer = useCustomer()
  const zipCodeName = isUSLocation ? 'Zip' : 'Postal'

  const { register, trigger, watch, setValue } = useFormContext()
  const payByEnteredCard = watch(`${gateway.field}.payByEnteredCard`)
  const expirationRef = useInputMask('99/99', {
    showMaskOnHover: false,
    showMaskOnFocus: false,
    placeholder: '0',
    register,
    rules: {
      required: {
        value: payByEnteredCard,
        message: 'Expiration required',
      },
      validate: {
        expiration: (value: string) => {
          if (!payByEnteredCard) return
          const [month, year] = value.split('/')
          const isValid =
            MIN_MONTH_NUMBER <= +month &&
            +month <= MAX_MONTH_NUMBER &&
            (+year > TWO_FIRST_YEAR_DIGITS || (+year === TWO_FIRST_YEAR_DIGITS && +month > new Date().getUTCMonth()))
          return isValid ? undefined : 'Enter a valid expiration date'
        },
      },
    },
  })
  const zipCodeRef = useRef<HTMLInputElement>(null)

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = useEvent((e) => trigger(e.target.name))
  const handleCardNumberValid = useEvent(() => expirationRef.current?.focus())
  const handleCVVValid = useEvent(() => zipCodeRef.current?.focus())
  const handleExpirationChange = useEvent(async () => {
    if (expirationRef.current?.inputmask?.isComplete() && (await trigger('spreedlyTokenize.expiration'))) {
      transferFocus('cvv')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })

  useEffect(
    () =>
      register(zipCodeRef.current, {
        required: {
          value: payByEnteredCard,
          message: `${zipCodeName} required`,
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payByEnteredCard],
  )
  useEffect(() => {
    setValue('spreedlyTokenize.first_name', customer?.firstName)
    setValue('spreedlyTokenize.last_name', customer?.lastName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.firstName, customer?.lastName])

  return (
    <div className="checkout-card" id="form-container">
      {paymentStatus.isLoading ? <SpinnerWheel additionalClassNames="spinner-wheel-black spinner-wheel-50" /> : null}
      <div className="checkout-card__body" hidden={!paymentStatus.isReady || !payByEnteredCard}>
        <div className={styles.body}>
          <NumberInput
            className={`${styles.cardNumber} form-control`}
            format="prettyFormat"
            handleValid={handleCardNumberValid}
            iconClass={styles.cardIcon}
            style="font-size: 16px; width: 100%; box-sizing: border-box; height: 45px;"
            wrapperClass={styles.cardWrapper}
          />
          <div className={styles.cardData}>
            <input
              ref={expirationRef}
              className={`${styles.expiration} form-control`}
              id="spreedly-expiration"
              maxLength={5}
              name="spreedlyTokenize.expiration"
              placeholder="MM/YY"
              onBlur={handleBlur}
              onChange={handleExpirationChange}
            />
            <CVVInput
              className={`${styles.cvv} form-control`}
              handleValid={handleCVVValid}
              style="font-size: 16px; width: 100%; text-align: center; box-sizing: border-box; height: 45px;"
            />
            <input
              ref={zipCodeRef}
              className={`${styles.postal} form-control`}
              id="spreedly-zip"
              name="spreedlyTokenize.zip"
              placeholder={zipCodeName}
              type="text"
            />
          </div>
        </div>

        <input
          ref={register({ required: payByEnteredCard })}
          className={`${styles.firstName} form-control`}
          id="spreedly-first-name"
          name="spreedlyTokenize.first_name"
          placeholder="First name"
          type="text"
          onBlur={handleBlur}
        />

        <input
          ref={register({ required: payByEnteredCard })}
          className={`${styles.lastName} form-control`}
          id="spreedly-last-name"
          name="spreedlyTokenize.last_name"
          placeholder="Last name"
          type="text"
          onBlur={handleBlur}
        />

        <ErrorMessage
          as={FormErrors}
          name={[
            'cardNumber',
            'spreedlyTokenize.number',
            'spreedlyTokenize.expiration',
            'cvv',
            'spreedlyTokenize.zip',
            'spreedlyTokenize.first_name',
            'spreedlyTokenize.last_name',
            'spreedlyPayment.sourceId',
          ]}
        />

        {customer?.signedUp ? (
          <div className="custom-control / custom-checkbox / mt-1">
            <input
              ref={register}
              className="custom-control-input"
              id="retain"
              name="spreedlyPayment.retainOnSuccess"
              type="checkbox"
            />
            <label className="custom-control-label" htmlFor="retain">
              Save card information for future purchases
            </label>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default SpreedlyPaymentFields
